import { GUI } from 'dat.gui'
import {
  BlendFunction,
  EdgeDetectionMode,
  EffectComposer,
  PredicationMode,
  SMAAEffect,
  SMAAPreset,
} from 'postprocessing'
import { WebGLRenderer } from 'three'
import { SMAADto } from '../../../../../trillion-common/src/dto/posteffects/smaa.dto'

import { SMAA } from '../../../../../trillion-common/src/objects/posteffects/smaa.object'
import { StoreScheme } from '../../../shared/store/store'

type IAAMode = {
  DISABLED: number
  SMAA: number
  MSAA: number
  BOTH: number
}

export function addAntiAliasingGUI(
  gui: GUI,
  composer: EffectComposer,
  renderer: WebGLRenderer,
  smaaEffect: SMAAEffect,
  smaaConfig: SMAADto | undefined,
  store: StoreScheme,
) {
  const setSmaa = store.getState().setSmaa

  const edgeDetectionMaterial = smaaEffect.edgeDetectionMaterial

  const AAMode: IAAMode = {
    DISABLED: 0,
    SMAA: 1,
    MSAA: 2,
    BOTH: 3,
  }

  const smaaOptions = {
    antialiasing: smaaConfig?.antialiasing ?? AAMode.MSAA,

    smaaPreset: smaaConfig?.smaaPreset ?? SMAAPreset.HIGH,
    smaaPresetOpacity:
      smaaConfig?.smaaPresetOpacity ?? (smaaEffect.blendMode.opacity.value as number),
    smaaPresetBlendMode: smaaEffect.blendMode.blendFunction,

    edgeDetectionMode: Number(edgeDetectionMaterial.defines.EDGE_DETECTION_MODE),
    edgeContrastFactor: Number(edgeDetectionMaterial.defines.LOCAL_CONTRAST_ADAPTATION_FACTOR),
    edgeThreshold: Number(edgeDetectionMaterial.defines.EDGE_THRESHOLD),

    predicationMode: Number(edgeDetectionMaterial.defines.PREDICATION_MODE),
    predicationThreshold: Number(edgeDetectionMaterial.defines.PREDICATION_THRESHOLD),
    predicationStrength: Number(edgeDetectionMaterial.defines.PREDICATION_STRENGTH),
    predicationScale: Number(edgeDetectionMaterial.defines.PREDICATION_SCALE),
  }

  const aaFolder = gui.addFolder('Anti Aliasing')
  aaFolder.open()

  aaFolder.add(smaaOptions, 'antialiasing', AAMode).onChange((value) => {
    const mode = Number(value)

    smaaEffect.blendMode.blendFunction =
      mode === AAMode.SMAA || mode === AAMode.BOTH ? BlendFunction.SET : BlendFunction.SKIP

    composer.multisampling =
      mode === AAMode.MSAA || mode === AAMode.BOTH ? renderer.capabilities.maxSamples : 0
  })

  const smaaFolder = aaFolder.addFolder('SMAA')

  smaaFolder
    .add(smaaOptions, 'smaaPreset', SMAAPreset)
    .name('preset')
    .onChange((value) => {
      const numValue = Number(value)
      smaaEffect.applyPreset(numValue)
      edgeDetectionMaterial.edgeDetectionThreshold = smaaOptions.edgeThreshold
      updateSmaaEffect((smaa) => {
        smaa.smaaPreset = numValue
      })
    })

  let subfolder = smaaFolder.addFolder('Edge Detection')
  subfolder
    .add(smaaOptions, 'edgeDetectionMode', EdgeDetectionMode)
    .name('mode')
    .onChange((value) => {
      const numValue = Number(value)
      edgeDetectionMaterial.edgeDetectionMode = numValue
      updateSmaaEffect((smaa) => {
        smaa.edgeDetectionMode = numValue
      })
    })
  subfolder
    .add(smaaOptions, 'edgeContrastFactor', 1.0, 3.0, 0.01)
    .name('contrast factor')
    .onChange((value: number) => {
      edgeDetectionMaterial.localContrastAdaptationFactor = value
      updateSmaaEffect((smaa) => {
        smaa.edgeContrastFactor = value
      })
    })
  subfolder
    .add(smaaOptions, 'edgeThreshold', 0.0, 0.5, 0.0001)
    .name('threshold')
    .onChange((value: number) => {
      edgeDetectionMaterial.edgeDetectionThreshold = value
      updateSmaaEffect((smaa) => {
        smaa.edgeThreshold = value
      })
    })

  subfolder = smaaFolder.addFolder('Predicated Thresholding')
  subfolder
    .add(smaaOptions, 'predicationMode', PredicationMode)
    .name('mode')
    .onChange((value) => {
      const numValue = Number(value)
      edgeDetectionMaterial.predicationMode = numValue
      updateSmaaEffect((smaa) => {
        smaa.predicationMode = numValue
      })
    })
  subfolder
    .add(smaaOptions, 'predicationThreshold', 0.0, 0.5, 0.0001)
    .name('threshold')
    .onChange((value: number) => {
      edgeDetectionMaterial.predicationThreshold = value
      updateSmaaEffect((smaa) => {
        smaa.predicationThreshold = value
      })
    })
  subfolder
    .add(smaaOptions, 'predicationStrength', 0.0, 1.0, 0.0001)
    .name('strength')
    .onChange((value: number) => {
      edgeDetectionMaterial.predicationStrength = value
      updateSmaaEffect((smaa) => {
        smaa.predicationStrength = value
      })
    })
  subfolder
    .add(smaaOptions, 'predicationScale', 1.0, 5.0, 0.01)
    .name('scale')
    .onChange((value: number) => {
      edgeDetectionMaterial.predicationScale = value
      updateSmaaEffect((smaa) => {
        smaa.predicationScale = value
      })
    })

  smaaFolder
    .add(smaaOptions, 'smaaPresetOpacity', 0.0, 1.0, 0.01)
    .name('opacity')
    .onChange((value: number) => {
      smaaEffect.blendMode.opacity.value = value
      updateSmaaEffect((smaa) => {
        smaa.smaaPresetOpacity = value
      })
    })
  smaaFolder
    .add(smaaOptions, 'smaaPresetBlendMode', BlendFunction)
    .name('blend mode')
    .onChange((value) => {
      const numValue = Number(value)
      smaaEffect.blendMode.blendFunction = numValue
      updateSmaaEffect((smaa) => {
        smaa.smaaPresetBlendMode = numValue
      })
    })

  function updateSmaaEffect(updateCallback: (smaa: SMAA) => void) {
    updateCallback(smaaOptions)
    setSmaa(smaaOptions)
  }

  setSmaa(smaaOptions)
}
